import {
  type FragmentID,
  type FragmentLookup,
  type FragmentPrefix,
  type ReadonlyFragment,
  type ReadonlyResource,
  type ReadonlyResourceParent,
  type ResourceID,
  type ResourceParent,
  ResourceTypes,
  type SingletonResourceType,
} from '../config-item';
import { getFragmentImport } from '../get-part';
import type { SingletonResourceFragmentSummary } from '../singleton-resource';

export const PathByType = {
  [ResourceTypes.COMPONENT]: 'components',
  [ResourceTypes.SERVICE]: 'services',
  [ResourceTypes.REGISTRY_MODULE]: 'registryModules',
  [ResourceTypes.LOCAL_MODULE]: 'localModules',
  [ResourceTypes.REMOTE]: 'remotes',
  [ResourceTypes.PROCESS]: 'processes',
  [ResourceTypes.PACKAGE]: 'packages',
  [ResourceTypes.TRIGGER]: 'triggers',
  [ResourceTypes.JOB]: 'jobs',
  [ResourceTypes.FRAGMENT_IMPORT]: undefined,
} as const;

export interface OriginFragmentAndResource<
  R extends ReadonlyResource = ReadonlyResource,
> {
  fragment: ReadonlyFragment;
  resource: R;
}

export const getOriginFragment = <
  R extends ReadonlyResource = ReadonlyResource,
>(
  part: ResourceParent,
  fragments: FragmentLookup,
  resourceID: ResourceID,
  resourceType: R['type']
): OriginFragmentAndResource<R> | undefined => {
  const parentPath = PathByType[resourceType];

  if (!parentPath || part[parentPath][resourceID]) {
    return undefined;
  }

  for (const prefixedFragments of Object.values(fragments)) {
    for (const fragment of Object.values(prefixedFragments)) {
      const resource = fragment.effectiveConfig[parentPath][resourceID];
      if (resource) {
        return { fragment, resource: resource as R };
      }
    }
  }

  return undefined;
};

/** Returns a list of fragment IDs that provide item configs */
export const getOriginFragmentIDAndPrefixes = (
  itemType: SingletonResourceType,
  originalFragments: ReadonlyFragment[]
): { fragmentID: FragmentID; prefix?: FragmentPrefix }[] => {
  return originalFragments
    .filter((fragment) => fragment.effectiveConfig[itemType] !== undefined)
    .map((fragment) => ({
      fragmentID: fragment.id,
      prefix: fragment.prefix,
    }));
};

/** Returns a list of fragment summaries that provide item configs */
export const getOriginFragmentSummaries = (
  itemType: SingletonResourceType,
  originalFragments: readonly ReadonlyFragment[],
  resourceParent: ReadonlyResourceParent
): SingletonResourceFragmentSummary[] => {
  return originalFragments
    .filter((fragment) => fragment.effectiveConfig[itemType] !== undefined)
    .map((fragment) => ({
      fragmentID: fragment.id,
      fragmentName: fragment.name,
      organizationName: fragment.organizationName,
      fragmentImport: getFragmentImport(
        resourceParent,
        fragment.id,
        fragment.prefix
      ),
      variablePaths:
        fragment.variablePaths?.find(
          (vp) =>
            vp.resourceSection === itemType && vp.resourceName === itemType
        )?.paths ?? {},
    }))
    .filter(
      (summary): summary is SingletonResourceFragmentSummary =>
        summary.fragmentImport !== undefined
    );
};
